import dayjs from "dayjs";
import {
  CancelRounded,
  DeleteOutlined,
  SearchSharp,
  SyncOutlined,
  WorkOutlineSharp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";

import AddressForm from "../../components/crm/AddressForm";
import CustomerDetailView from "../../components/crm/CustomerDetailView";
import CustomerForm from "../../components/crm/CustomerForm";
import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import CustomDialog from "../../components/CustomDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import useCrmAddresses from "../../hooks/useCrmAddresses";
import useCrmCustomers from "../../hooks/useCrmCustomers";
import useCrmOrders from "../../hooks/useCrmOrders";
import {
  addCustomerPoints,
  createAddress,
  deleteAddress,
  deleteCustomer,
  getCustomerECPShipTo,
  redeemCustomerPoints,
  updateAddress,
  updateCustomer,
} from "../../services/crm.service";
import {
  Customer,
  CustomerAddress,
  CustomerAddressCreateRequest,
  CustomerECPShipTo,
  CustomerListResponse,
  CustomerUpdateRequest,
  UpdateCustomerAddressRequest,
} from "../../types/crm";
import type { Order, OrderFilters, OrderStatus } from "../../types/order";

const monthOptions = [
  { label: "All Month", value: "all" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const currentYear = dayjs().year();
const yearOptions = Array.from({ length: 3 }, (_, i) => {
  const y = String(currentYear - i);
  return { label: y, value: y };
});

const ADMIN_ACTION_PREFIX = "Admin action:";

function isAdminAction(reason: string | null | undefined): boolean {
  if (!reason) return false;
  return reason.toLowerCase().startsWith(ADMIN_ACTION_PREFIX.toLowerCase());
}

function stripAdminPrefix(reason: string): string {
  return reason.slice(ADMIN_ACTION_PREFIX.length).trim();
}

export default function CrmCustomerDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addAddressDialogOpen, setAddAddressDialogOpen] = useState(false);
  const [editAddressDialogOpen, setEditAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(
    null,
  );
  const [defaultAddressDialogOpen, setDefaultAddressDialogOpen] =
    useState(false);
  const [pendingDefaultAddress, setPendingDefaultAddress] =
    useState<CustomerAddress | null>(null);
  const [deleteAddressDialogOpen, setDeleteAddressDialogOpen] = useState(false);
  const [deletingAddress, setDeletingAddress] =
    useState<CustomerAddress | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>("");
  const [changeStatusRemark, setChangeStatusRemark] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderFilters, setOrderFilters] = useState({
    search: "",
    month: "all",
    year: String(currentYear),
    status: "all",
    page: 0,
    pageSize: 10,
  });
  const [pointTab, setPointTab] = useState<"used" | "earned" | "adjusted">(
    "used",
  );
  const [pointFilters, setPointFilters] = useState({
    search: "",
    month: "all",
    year: String(currentYear),
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setBreadcrumbs([
      { label: "Customer List", to: "/crm/customers" },
      { label: "Customer Profile", to: "" },
    ]);
  }, [setBreadcrumbs]);

  const { data, dataLoading, mutate } = useCrmCustomers(
    Boolean(id),
    { fetchOne: true, id: id || "" },
    {},
  );

  const customer = useMemo<Customer | null>(() => {
    if (!data || !id) return null;

    if (Array.isArray((data as CustomerListResponse).data)) {
      return (
        (data as CustomerListResponse).data.find((item) => item.id === id) ||
        null
      );
    }

    return data as Customer;
  }, [data, id]);

  const filteredPointsHistory = useMemo(() => {
    if (!customer?.points_history) return [];
    return customer.points_history.filter((item) => {
      if (pointTab === "earned" && !(item.type === "earn" && !isAdminAction(item.reason))) return false;
      if (pointTab === "used" && !(item.type === "redeem" && !isAdminAction(item.reason))) return false;
      if (pointTab === "adjusted" && !isAdminAction(item.reason)) return false;
      if (pointFilters.search) {
        const q = pointFilters.search.toLowerCase();
        const displayReason = isAdminAction(item.reason)
          ? stripAdminPrefix(item.reason ?? "").toLowerCase()
          : (item.reason ?? "").toLowerCase();
        if (!displayReason.includes(q)) return false;
      }
      if (pointFilters.month !== "all") {
        const m = dayjs(item.transaction_date).format("MM");
        if (m !== pointFilters.month) return false;
      }
      const y = dayjs(item.transaction_date).format("YYYY");
      if (y !== pointFilters.year) return false;
      return true;
    });
  }, [
    customer?.points_history,
    pointTab,
    pointFilters.search,
    pointFilters.month,
    pointFilters.year,
  ]);

  const {
    data: addressData,
    dataLoading: addressLoading,
    mutate: mutateAddresses,
  } = useCrmAddresses(Boolean(customer?.ecp_code), null, {
    ecp_code: customer?.ecp_code || "",
  });

  const {
    data: shipToData,
    isLoading: shipToLoading,
    mutate: mutateShipTo,
  } = useSWR<CustomerECPShipTo[]>(
    customer?.id ? `customers/${customer.id}/ecp/ship-to` : null,
    () => getCustomerECPShipTo(customer!.id),
  );

  const handleMutateAddresses = () => {
    mutateAddresses();
    mutateShipTo();
  };

  const orderApiFilters = useMemo<OrderFilters>(() => {
    const hasSpecificMonth = orderFilters.month !== "all";
    const selectedYear = Number(orderFilters.year);
    const selectedMonth = Number(orderFilters.month) - 1;
    const timeRange =
      Number.isFinite(selectedYear) && selectedYear > 0
        ? {
            time_from: hasSpecificMonth
              ? dayjs()
                  .year(selectedYear)
                  .month(selectedMonth)
                  .startOf("month")
                  .toISOString()
              : dayjs().year(selectedYear).startOf("year").toISOString(),
            time_to: hasSpecificMonth
              ? dayjs()
                  .year(selectedYear)
                  .month(selectedMonth)
                  .endOf("month")
                  .toISOString()
              : dayjs().year(selectedYear).endOf("year").toISOString(),
          }
        : {};
    return {
      ecp_code: customer?.ecp_code || undefined,
      search: orderFilters.search || undefined,
      page: orderFilters.page + 1,
      page_size: orderFilters.pageSize,
      ...(orderFilters.status !== "all"
        ? { statuses: [orderFilters.status as OrderStatus] }
        : {}),
      sort: ["id|-1"],
      ...timeRange,
    };
  }, [orderFilters, customer?.ecp_code]);

  const {
    data: orderData,
    dataLoading: orderLoading,
    getStatusChip,
  } = useCrmOrders(Boolean(customer?.ecp_code), orderApiFilters);

  const { showSnackbar } = useSnackbar();

  const handleEditSubmit = async (formData: CustomerUpdateRequest) => {
    if (!customer?.id) return;
    setIsSubmitting(true);
    try {
      await updateCustomer(customer.id, formData);
      setEditDialogOpen(false);
      mutate();
      showSnackbar("Customer updated successfully", "success");
    } catch (error) {
      console.error("Error saving customer:", error);
      showSnackbar("Failed to update customer", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAddress = async (formData: CustomerAddressCreateRequest) => {
    if (!customer?.ecp_code) return;
    await createAddress(formData, customer.ecp_code);
    setAddAddressDialogOpen(false);
    handleMutateAddresses();
    showSnackbar("Address created successfully", "success");
  };

  const handleEditAddress = async (formData: UpdateCustomerAddressRequest) => {
    if (!editingAddress?.id) return;
    await updateAddress(editingAddress.id, formData);
    setEditAddressDialogOpen(false);
    setEditingAddress(null);
    handleMutateAddresses();
    showSnackbar("Address updated successfully", "success");
  };

  const handleSetDefaultAddress = async () => {
    if (!pendingDefaultAddress?.id) return;
    setIsSubmitting(true);
    try {
      const currentDefault = (addressData?.data || [])
        .filter((a: { status: string; }) => a.status === "active")
        .find((a: { default: any; id: string; }) => a.default && a.id !== pendingDefaultAddress.id);
      if (currentDefault) {
        await updateAddress(currentDefault.id, { default: false });
      }
      await updateAddress(pendingDefaultAddress.id, { default: true });
      setDefaultAddressDialogOpen(false);
      setPendingDefaultAddress(null);
      handleMutateAddresses();
      showSnackbar("Default address updated successfully", "success");
    } catch (error) {
      console.error("Error setting default address:", error);
      showSnackbar("Failed to set default address", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAddress = async () => {
    if (!deletingAddress?.id) return;
    setIsSubmitting(true);
    try {
      await deleteAddress(deletingAddress.id);
      setDeleteAddressDialogOpen(false);
      setDeletingAddress(null);
      handleMutateAddresses();
      showSnackbar("Address deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting address:", error);
      showSnackbar("Failed to delete address", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!customer?.id) return;
    setIsSubmitting(true);
    try {
      await updateCustomer(customer.id, { status: "active" });
      setApproveDialogOpen(false);
      mutate();
      showSnackbar("Customer approved successfully", "success");
    } catch (error) {
      console.error("Error approving customer:", error);
      showSnackbar("Failed to approve customer", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!customer?.id || !rejectRemark.trim()) return;
    setIsSubmitting(true);
    try {
      await updateCustomer(customer.id, {
        status: "rejected",
        remark: rejectRemark,
      });
      setRejectDialogOpen(false);
      setRejectRemark("");
      mutate();
      showSnackbar("Customer rejected successfully", "success");
    } catch (error) {
      console.error("Error rejecting customer:", error);
      showSnackbar("Failed to reject customer", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!customer?.id) return;
    setIsSubmitting(true);
    try {
      await deleteCustomer(customer.id);
      setDeleteDialogOpen(false);
      showSnackbar("Customer deleted successfully", "success");
      navigate("/crm/customers");
    } catch (error) {
      console.error("Error deleting customer:", error);
      showSnackbar("Failed to delete customer", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStatus = async () => {
    if (!customer?.id || !newStatus || newStatus === customer.status) return;
    setIsSubmitting(true);
    try {
      await updateCustomer(customer.id, {
        status: newStatus as any,
        ...(changeStatusRemark.trim() ? { remark: changeStatusRemark } : {}),
      });
      setChangeStatusDialogOpen(false);
      setNewStatus("");
      setChangeStatusRemark("");
      mutate();
      showSnackbar("Customer status updated successfully", "success");
    } catch (error) {
      console.error("Error changing status:", error);
      showSnackbar("Failed to change customer status", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusChipStyles: Record<string, { bgcolor: string; color: string }> = {
    active: { bgcolor: "#E3F5E3", color: "#2D6830" },
    inactive: { bgcolor: "#FFFBC5", color: "#BB6902" },
    in_monitor: { bgcolor: "#FFFBC5", color: "#BB6902" },
    rejected: { bgcolor: "#FFE3E1", color: "#BE2217" },
    suspended: { bgcolor: "#FFE3E1", color: "#BE2217" },
  };

  const getStatusOptions = () => {
    switch (customer?.status) {
      case "inactive":
        return [
          { label: "Active", value: "active" },
          { label: "Rejected", value: "rejected" },
        ];
      case "active":
        return [
          { label: "In Monitor", value: "in_monitor" },
          { label: "Suspended", value: "suspended" },
        ];
      case "in_monitor":
        return [
          { label: "Active", value: "active" },
          { label: "Suspended", value: "suspended" },
        ];
      case "suspended":
      case "rejected":
        return [
          { label: "Active", value: "active" },
        ];
      default:
        return [];
    }
  };

  const allStatusOptions = getStatusOptions();

  if (dataLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 320 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  if (!customer) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Typography sx={{ fontSize: 16, color: "#8E95A2" }}>
          Customer not found.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ px: 2, py: 1.5, borderBottom: "1px solid #D8DBDF" }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
            Overview Customer
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {customer.status === "inactive" ? (
              <>
                {/* <Button
                  variant="outlined"
                  startIcon={<DeleteOutlined />}
                  disabled={isSubmitting}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    height: 40,
                    borderRadius: "96px",
                    borderColor: "#BE2217",
                    color: "#BE2217",
                    fontWeight: 700,
                    fontSize: 16,
                    textTransform: "none",
                  }}
                >
                  Delete
                </Button> */}
                <Button
                  variant="contained"
                  startIcon={<CancelRounded />}
                  disabled={isSubmitting}
                  onClick={() => setRejectDialogOpen(true)}
                  sx={{
                    height: 40,
                    borderRadius: "96px",
                    bgcolor: "#BE2217",
                    fontWeight: 700,
                    fontSize: 16,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#A31B12" },
                  }}
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  disabled={isSubmitting}
                  onClick={() => setApproveDialogOpen(true)}
                  sx={{
                    height: 40,
                    px: 3,
                    borderRadius: "100px",
                    bgcolor: "#D4EDFF",
                    color: "#0054FC",
                    fontWeight: 700,
                    fontSize: 16,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#C0E3FF", boxShadow: "none" },
                  }}
                >
                  Approve
                </Button>
              </>
            ) : (
              <>
                {/* <Button
                  variant="contained"
                  startIcon={<DeleteOutlined />}
                  disabled={isSubmitting}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    height: 40,
                    borderRadius: "96px",
                    bgcolor: "#BE2217",
                    fontWeight: 700,
                    fontSize: 16,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#A31B12" },
                  }}
                >
                  Delete
                </Button> */}
                <Button
                  variant="contained"
                  startIcon={<SyncOutlined />}
                  disabled={isSubmitting}
                  onClick={() => {
                    setNewStatus("");
                    setChangeStatusRemark("");
                    setChangeStatusDialogOpen(true);
                  }}
                  sx={{
                    height: 40,
                    borderRadius: "96px",
                    bgcolor: "#111",
                    fontWeight: 700,
                    fontSize: 16,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#333" },
                  }}
                >
                  Change Status
                </Button>
              </>
            )}
          </Stack>
        </Stack>
        <Box sx={{ p: 2 }}>
          <CustomerDetailView
            customer={customer}
            addresses={(addressData?.data || []).filter((a: { status: string; }) => a.status === "active")}
            addressLoading={addressLoading}
            shipToData={shipToData}
            shipToLoading={shipToLoading}
            onEditDetail={() => setEditDialogOpen(true)}
            onAddAddress={() => setAddAddressDialogOpen(true)}
            onEditAddress={(addr) => {
              setEditingAddress(addr);
              setEditAddressDialogOpen(true);
            }}
            onSetDefaultAddress={(addr) => {
              setPendingDefaultAddress(addr);
              setDefaultAddressDialogOpen(true);
            }}
            onDeleteAddress={(addr) => {
              setDeletingAddress(addr);
              setDeleteAddressDialogOpen(true);
            }}
            onEditPoints={async (data) => {
              if (!customer?.id) return;
              try {
                if (data.mode === "increase") {
                  await addCustomerPoints(customer.id, {
                    points: data.points,
                    reason: `Admin action:${data.reason}`,
                    expires_on: data.expirationDate!,
                    send_notification: data.sendNotification,
                  });
                } else {
                  await redeemCustomerPoints(customer.id, {
                    points: data.points,
                    reason: `Admin action:${data.reason}`,
                    send_notification: data.sendNotification,
                  });
                }
                await mutate();
                showSnackbar("Customer points updated successfully", "success");
              } catch (error) {
                console.error("Error editing customer points:", error);
                showSnackbar("Failed to update customer points", "error");
                throw error;
              }
            }}
          />
        </Box>
      </Paper>

      <Stack spacing={3}>
        <Typography
          sx={{
            fontSize: { xs: 24, md: 32 },
            fontWeight: 700,
            lineHeight: 1.4,
          }}
        >
          Order History
        </Typography>
        <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ p: 2, borderBottom: "1px solid #D8DBDF" }}
          >
            <TextField
              size="small"
              placeholder="Search"
              value={orderFilters.search}
              onChange={(e) =>
                setOrderFilters((prev) => ({ ...prev, search: e.target.value }))
              }
              sx={{ width: { xs: "100%", sm: 180 } }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchSharp
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              size="small"
              value={orderFilters.month}
              onChange={(e) =>
                setOrderFilters((prev) => ({ ...prev, month: e.target.value }))
              }
              sx={{ width: { xs: "calc(50% - 6px)", sm: 180 } }}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={orderFilters.year}
              onChange={(e) =>
                setOrderFilters((prev) => ({ ...prev, year: e.target.value }))
              }
              sx={{ width: { xs: "calc(50% - 6px)", sm: 180 } }}
            >
              {yearOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={orderFilters.status}
              onChange={(e) =>
                setOrderFilters((prev) => ({ ...prev, status: e.target.value }))
              }
              sx={{ width: { xs: "100%", sm: 180 } }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="RECEIVED">Received</MenuItem>
              <MenuItem value="CREATED">Created</MenuItem>
              <MenuItem value="REQUEST_MORE_INFO">Request More Info</MenuItem>
              <MenuItem value="UPDATED">Updated</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
          </Stack>
          {orderLoading ? (
            <TableContainer>
              <Table
                sx={{ minWidth: 900, "& td, & th": { whiteSpace: "nowrap" } }}
              >
                <TableHead>
                  <TableRow>
                    {["No.", "Order ID", "Status", "ECP Code", "Items", "Order Time"].map(
                      (header) => (
                        <TableCell key={header} sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase" }}>
                          {header}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, rowIndex) => (
                    <TableRow key={`skeleton-${rowIndex}`}>
                      {Array.from({ length: 6 }).map((__, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : !orderData?.data?.length ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ minHeight: 300, py: 6 }}
            >
              <WorkOutlineSharp sx={{ color: "#9BA4B4", fontSize: 40 }} />
              <Typography
                sx={{
                  color: "#8E95A2",
                  fontSize: 16,
                  textAlign: "center",
                }}
              >
                No order history found for the selected period.
              </Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table
                sx={{ minWidth: 900, "& td, & th": { whiteSpace: "nowrap" } }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "No.",
                      "Order ID",
                      "Status",
                      "ECP Code",
                      "Items",
                      "Order Time",
                      "Updated",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(orderData.data as Order[]).map((order, idx) => (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        order.id && navigate(`/crm/orders/${order.id}/edit`)
                      }
                    >
                      <TableCell>
                        {(orderFilters.page * orderFilters.pageSize) + idx + 1}
                      </TableCell>
                      <TableCell>{order.order_id || "-"}</TableCell>
                      <TableCell>{getStatusChip(order.status)}</TableCell>
                      <TableCell>
                        {order.customer?.ecp_code || customer?.ecp_code || "-"}
                      </TableCell>
                      <TableCell>
                        {1 + (order.addons?.length ?? 0)}
                      </TableCell>
                      <TableCell>
                        {order.created_at
                          ? dayjs(order.created_at).format("DD/MM/YYYY HH:mm")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {order.updated_at
                          ? dayjs(order.updated_at).format("DD/MM/YYYY HH:mm")
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={orderData?.total_items ?? 0}
            page={orderFilters.page}
            rowsPerPage={orderFilters.pageSize}
            onPageChange={(_, page) =>
              setOrderFilters((prev) => ({ ...prev, page }))
            }
            onRowsPerPageChange={(e) =>
              setOrderFilters((prev) => ({
                ...prev,
                pageSize: parseInt(e.target.value, 10),
                page: 0,
              }))
            }
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      </Stack>

      <Stack spacing={3}>
        <Typography
          sx={{
            fontSize: { xs: 24, md: 32 },
            fontWeight: 700,
            lineHeight: 1.4,
          }}
        >
          History Points
        </Typography>
        <Paper
          sx={{
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid #D8DBDF",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ p: 2, borderBottom: "1px solid #D8DBDF" }}
          >
            <TextField
              size="small"
              placeholder="Search"
              value={pointFilters.search}
              onChange={(e) =>
                setPointFilters((prev) => ({
                  ...prev,
                  search: e.target.value,
                  page: 0,
                }))
              }
              sx={{ width: { xs: "100%", sm: 180 } }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchSharp
                        sx={{ color: "text.secondary", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              size="small"
              value={pointFilters.month}
              onChange={(e) =>
                setPointFilters((prev) => ({
                  ...prev,
                  month: e.target.value,
                  page: 0,
                }))
              }
              sx={{ width: { xs: "calc(50% - 6px)", sm: 180 } }}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={pointFilters.year}
              onChange={(e) =>
                setPointFilters((prev) => ({
                  ...prev,
                  year: e.target.value,
                  page: 0,
                }))
              }
              sx={{ width: { xs: "calc(50% - 6px)", sm: 180 } }}
            >
              {yearOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
          </Stack>

          <Tabs
            value={pointTab}
            onChange={(_, v) => {
              setPointTab(v);
              setPointFilters((prev) => ({ ...prev, page: 0 }));
            }}
            variant="fullWidth"
            sx={{
              borderBottom: "1px solid #D8DBDF",
              minHeight: 48,
              "& .MuiTabs-indicator": {
                bgcolor: "#111111",
                height: 3,
                borderRadius: "2px",
              },
            }}
          >
            <Tab
              value="used"
              label="Points Redeemed"
              sx={{
                fontSize: { xs: 12, sm: 14, md: 16 },
                fontWeight: 700,
                textTransform: "none",
                color: "#8E95A2",
                "&.Mui-selected": { color: "#111111" },
              }}
            />
            <Tab
              value="earned"
              label="Points Earned"
              sx={{
                fontSize: { xs: 12, sm: 14, md: 16 },
                fontWeight: 700,
                textTransform: "none",
                color: "#8E95A2",
                "&.Mui-selected": { color: "#111111" },
              }}
            />
            <Tab
              value="adjusted"
              label="Adjusted By Admin"
              sx={{
                fontSize: { xs: 12, sm: 14, md: 16 },
                fontWeight: 700,
                textTransform: "none",
                color: "#8E95A2",
                "&.Mui-selected": { color: "#111111" },
              }}
            />
          </Tabs>

          {filteredPointsHistory.length === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ minHeight: 300, py: 6 }}
            >
              <WorkOutlineSharp sx={{ color: "#9BA4B4", fontSize: 40 }} />
              <Typography
                sx={{
                  color: "#8E95A2",
                  fontSize: 16,
                  textAlign: "center",
                }}
              >
                {pointTab === "used"
                  ? "No redeemed points found for the selected period."
                  : pointTab === "earned"
                    ? "No earned points found for the selected period."
                    : "No admin-adjusted points found for the selected period."}
              </Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table
                sx={{
                  minWidth: 900,
                  "& td, & th": { whiteSpace: "nowrap" },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "No.",
                      pointTab === "used"
                        ? "Redeemed For"
                        : pointTab === "earned"
                          ? "Earned From"
                          : "Reason",
                      "Detail",
                      "Points",
                      "Category",
                      pointTab === "used"
                        ? "Redeemed Time"
                        : pointTab === "earned"
                          ? "Earned Time"
                          : "Adjusted Time",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPointsHistory
                    .slice(
                      pointFilters.page * pointFilters.pageSize,
                      (pointFilters.page + 1) * pointFilters.pageSize,
                    )
                    .map((item, idx) => (
                      <TableRow
                        key={`${item.transaction_date}-${item.points}-${idx}`}
                      >
                        <TableCell>
                          {pointFilters.page * pointFilters.pageSize + idx + 1}
                        </TableCell>
                        <TableCell>
                          {isAdminAction(item.reason)
                            ? stripAdminPrefix(item.reason ?? "")
                            : (item.reason ?? "-")}
                        </TableCell>
                        <TableCell>
                          {item.expires_on
                            ? `Expires ${dayjs(item.expires_on).format("DD/MM/YYYY")}`
                            : item.remark || "-"}
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: item.type === "redeem" ? "#BE2217" : "#2D6830",
                            }}
                          >
                            {item.type === "redeem" ? "-" : "+"}
                            {item.points.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              isAdminAction(item.reason)
                                ? "Adjusted"
                                : item.type === "earn"
                                  ? "Earned"
                                  : "Redeemed"
                            }
                            sx={{
                              bgcolor:
                                isAdminAction(item.reason)
                                  ? "#FFFBC5"
                                  : item.type === "earn"
                                    ? "#E3F5E3"
                                    : "#FFE3E1",
                              color:
                                isAdminAction(item.reason)
                                  ? "#BB6902"
                                  : item.type === "earn"
                                    ? "#2D6830"
                                    : "#BE2217",
                              fontSize: 12,
                              fontWeight: 400,
                              borderRadius: "4px",
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {dayjs(item.transaction_date).format(
                            "DD/MM/YYYY HH:mm",
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={filteredPointsHistory.length}
            page={pointFilters.page}
            rowsPerPage={pointFilters.pageSize}
            onPageChange={(_, page) =>
              setPointFilters((prev) => ({ ...prev, page }))
            }
            onRowsPerPageChange={(e) =>
              setPointFilters((prev) => ({
                ...prev,
                pageSize: parseInt(e.target.value, 10),
                page: 0,
              }))
            }
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      </Stack>

      {/* <Stack spacing={3}>
        <Typography
          sx={{
            fontSize: { xs: 24, md: 32 },
            fontWeight: 700,
            lineHeight: 1.4,
          }}
        >
          Status History
        </Typography>
        <Paper
          sx={{
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid #D8DBDF",
          }}
        >
          {(customer.status_history?.length ?? 0) === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ minHeight: 200, py: 6 }}
            >
              <WorkOutlineSharp sx={{ color: "#9BA4B4", fontSize: 40 }} />
              <Typography sx={{ color: "#8E95A2", fontSize: 16 }}>
                No status history available.
              </Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table
                sx={{ minWidth: 600, "& td, & th": { whiteSpace: "nowrap" } }}
              >
                <TableHead>
                  <TableRow>
                    {["No.", "Status", "Remark", "Changed By", "Changed At"].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          {h}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...(customer.status_history ?? [])]
                    .sort(
                      (a, b) =>
                        new Date(b.changed_at).getTime() -
                        new Date(a.changed_at).getTime(),
                    )
                    .map((item, idx) => {
                      const chipMap: Record<
                        string,
                        { label: string; bgcolor: string; color: string }
                      > = {
                        active: {
                          label: "Active",
                          bgcolor: "#E3F5E3",
                          color: "#2D6830",
                        },
                        inactive: {
                          label: "Inactive",
                          bgcolor: "#EDEEF1",
                          color: "#6B7280",
                        },
                        in_monitor: {
                          label: "Under Monitor",
                          bgcolor: "#FFFBC5",
                          color: "#BB6902",
                        },
                        suspended: {
                          label: "Suspended",
                          bgcolor: "#FFE3E1",
                          color: "#BE2217",
                        },
                        rejected: {
                          label: "Rejected",
                          bgcolor: "#FFE3E1",
                          color: "#BE2217",
                        },
                      };
                      const chip = chipMap[item.status] ?? {
                        label: item.status,
                        bgcolor: "#EDEEF1",
                        color: "#6B7280",
                      };
                      return (
                        <TableRow key={idx}>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={chip.label}
                              sx={{
                                bgcolor: chip.bgcolor,
                                color: chip.color,
                                fontSize: 12,
                                fontWeight: 400,
                                borderRadius: "4px",
                                height: 24,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#6B7280" }}>
                            {item.remark || "-"}
                          </TableCell>
                          <TableCell>
                            {item.changed_by
                              ? item.changed_by.name ||
                                item.changed_by.role ||
                                item.changed_by.id
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {dayjs(item.changed_at).format("DD/MM/YYYY HH:mm")}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack> */}

      <CustomDialog
        open={editDialogOpen}
        title="Customer Edit Detail"
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        titleStyle={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
        content={
          <CustomerForm
            customer={customer}
            onSubmit={handleEditSubmit}
            onCancel={() => setEditDialogOpen(false)}
          />
        }
      />

      <CustomDialog
        open={addAddressDialogOpen}
        title="Create Shipping Address"
        onClose={() => setAddAddressDialogOpen(false)}
        maxWidth="sm"
        titleStyle={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
        content={
          <AddressForm
            ecpCode={customer?.ecp_code}
            onSubmit={handleAddAddress}
            onCancel={() => setAddAddressDialogOpen(false)}
          />
        }
      />

      <CustomDialog
        open={editAddressDialogOpen}
        title="Edit Shipping Address"
        onClose={() => {
          setEditAddressDialogOpen(false);
          setEditingAddress(null);
        }}
        maxWidth="sm"
        titleStyle={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
        content={
          editingAddress ? (
            <AddressForm
              address={editingAddress}
              ecpCode={customer?.ecp_code}
              onSubmit={handleEditAddress}
              onCancel={() => {
                setEditAddressDialogOpen(false);
                setEditingAddress(null);
              }}
            />
          ) : undefined
        }
      />

      <CustomConfirmDialog
        open={defaultAddressDialogOpen}
        title="Set Default Address"
        onClose={() => {
          setDefaultAddressDialogOpen(false);
          setPendingDefaultAddress(null);
        }}
        content={
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 16, color: "#25272C" }}>
              Are you sure you want to set{" "}
              <strong>
                {pendingDefaultAddress?.shop_name ||
                  `${pendingDefaultAddress?.name || ""} ${pendingDefaultAddress?.surname || ""}`.trim()}
              </strong>{" "}
              as the default shipping address?
            </Typography>
            {(addressData?.data || []).filter((a: any) => a.status === "active").some(
              (a: any) => a.default && a.id !== pendingDefaultAddress?.id,
            ) && (
              <Typography sx={{ fontSize: 14, color: "#8E95A2" }}>
                The current default address{" "}
                <strong>
                  {(() => {
                    const current = (addressData?.data || [])
                      .filter((a: any) => a.status === "active")
                      .find((a: any) => a.default && a.id !== pendingDefaultAddress?.id);
                    return (
                      current?.shop_name ||
                      `${current?.name || ""} ${current?.surname || ""}`.trim()
                    );
                  })()}
                </strong>{" "}
                will be unset.
              </Typography>
            )}
          </Stack>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setDefaultAddressDialogOpen(false);
                setPendingDefaultAddress(null);
              }}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSetDefaultAddress}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#111",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#333" },
              }}
            >
              Confirm
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={deleteAddressDialogOpen}
        title="Delete Shipping Address"
        onClose={() => {
          setDeleteAddressDialogOpen(false);
          setDeletingAddress(null);
        }}
        content={
          <Typography sx={{ fontSize: 16, color: "#25272C" }}>
            Are you sure you want to delete{" "}
            <strong>
              {deletingAddress?.shop_name ||
                `${deletingAddress?.name || ""} ${deletingAddress?.surname || ""}`.trim()}
            </strong>
            ? This action cannot be undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setDeleteAddressDialogOpen(false);
                setDeletingAddress(null);
              }}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteAddress}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#BE2217",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#A31B12" },
              }}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={approveDialogOpen}
        title="Approve Customer"
        onClose={() => setApproveDialogOpen(false)}
        content={
          <Typography sx={{ fontSize: 16, color: "#25272C" }}>
            Are you sure you want to approve <strong>{customer.name}</strong>?
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setApproveDialogOpen(false)}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApprove}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#2D6830",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#245626" },
              }}
            >
              Approve
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={rejectDialogOpen}
        title="Reject Customer"
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectRemark("");
        }}
        content={
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 16, color: "#25272C" }}>
              Details
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <TextField
              multiline
              minRows={4}
              fullWidth
              placeholder="Enter reason for rejection..."
              value={rejectRemark}
              onChange={(e) => setRejectRemark(e.target.value)}
              disabled={isSubmitting}
            />
          </Stack>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectRemark("");
              }}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleReject}
              disabled={isSubmitting || !rejectRemark.trim()}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#BE2217",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#A31B12" },
              }}
            >
              Reject
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={deleteDialogOpen}
        title="Delete Customer"
        onClose={() => setDeleteDialogOpen(false)}
        content={
          <Typography sx={{ fontSize: 16, color: "#25272C" }}>
            Are you sure you want to delete <strong>{customer.name}</strong>?
            This action cannot be undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              disabled={isSubmitting}
              sx={{
                height: 40,
                borderRadius: "96px",
                bgcolor: "#BE2217",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: "#A31B12" },
              }}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      {/* Change Status Dialog */}
      <CustomDialog
        open={changeStatusDialogOpen}
        title="Change Status"
        onClose={() => {
          setChangeStatusDialogOpen(false);
          setNewStatus("");
          setChangeStatusRemark("");
        }}
        maxWidth="sm"
        titleStyle={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
        content={
          <Stack spacing={3}>
            <Box>
              <Typography
                sx={{ fontSize: 16, lineHeight: 1.4, color: "#25272C", mb: 1 }}
              >
                Select Status
                <Typography component="span" sx={{ color: "#BE2217" }}>
                  *
                </Typography>
              </Typography>
              <TextField
                select
                fullWidth
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                disabled={isSubmitting}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => {
                    if (!value) {
                      return (
                        <Typography sx={{ color: "#9CA3AF", fontSize: 14 }}>
                          Select status
                        </Typography>
                      );
                    }
                    const selected = allStatusOptions.find(
                      (o) => o.value === value,
                    );
                    const chipStyle = statusChipStyles[value as string] || {
                      bgcolor: "#EDEEF1",
                      color: "#6B7280",
                    };
                    return (
                      <Chip
                        size="small"
                        label={selected?.label || (value as string)}
                        sx={{
                          height: 24,
                          borderRadius: "4px",
                          fontSize: 12,
                          fontWeight: 400,
                          ...chipStyle,
                        }}
                      />
                    );
                  },
                }}
              >
                {allStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Chip
                      size="small"
                      label={option.label}
                      sx={{
                        height: 24,
                        borderRadius: "4px",
                        fontSize: 12,
                        fontWeight: 400,
                        ...statusChipStyles[option.value],
                      }}
                    />
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {(newStatus === "in_monitor" || newStatus === "suspended" || newStatus === "rejected") && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 16,
                    lineHeight: 1.4,
                    color: "#25272C",
                    mb: 1,
                  }}
                >
                  Reason
                </Typography>
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  placeholder="Enter reason..."
                  value={changeStatusRemark}
                  onChange={(e) => setChangeStatusRemark(e.target.value)}
                  disabled={isSubmitting}
                />
              </Box>
            )}
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => {
                  setChangeStatusDialogOpen(false);
                  setNewStatus("");
                  setChangeStatusRemark("");
                }}
                disabled={isSubmitting}
                sx={{
                  height: 40,
                  borderRadius: "96px",
                  borderColor: "#111",
                  color: "#111",
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "none",
                  px: 3,
                }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                onClick={handleChangeStatus}
                disabled={isSubmitting || newStatus === customer.status}
                sx={{
                  height: 40,
                  borderRadius: "96px",
                  bgcolor: "#111",
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "none",
                  px: 3,
                  boxShadow:
                    "0px 1px 5px rgba(0,0,0,0.12), 0px 2px 2px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
                  "&:hover": { bgcolor: "#333" },
                }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        }
      />
    </Stack>
  );
}
